import React, { useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import PDFPageSelector from '../components/common/PDFPageSelector';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';

const DeletePages: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [selectedPages, setSelectedPages] = useState<number[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);

    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const handleFilesSelected = useCallback((selectedFiles: File[]) => {
        setFiles(selectedFiles);
        setSelectedPages([]);
        setProcessError(null);
    }, [setProcessError]);

    const handlePageSelectionChange = useCallback((pages: number[]) => {
        setSelectedPages(pages);
        setProcessError(null);
    }, [setProcessError]);

    const formatPageNumbers = (pages: number[]): string => {
        if (pages.length === 0) return '';

        const sortedPages = [...pages].sort((a, b) => a - b);
        const ranges: string[] = [];
        let start = sortedPages[0];
        let end = sortedPages[0];

        for (let i = 1; i <= sortedPages.length; i++) {
            if (i < sortedPages.length && sortedPages[i] === end + 1) {
                end = sortedPages[i];
            } else {
                if (start === end) {
                    ranges.push(`${start}`);
                } else {
                    ranges.push(`${start}-${end}`);
                }
                if (i < sortedPages.length) {
                    start = sortedPages[i];
                    end = sortedPages[i];
                }
            }
        }

        return ranges.join(',');
    };

    const handleDelete = async () => {
        if (files.length === 0) {
            setProcessError('请选择一个 PDF 文件');
            return;
        }

        if (selectedPages.length === 0) {
            setProcessError('请选择要删除的页面');
            return;
        }

        const pageNumbers = formatPageNumbers(selectedPages);
        await executeTask(files[0], 'delete_pages', { pageNumbers });
    };

    const handleReset = () => {
        resetTask();
        setFiles([]);
        setSelectedPages([]);
    };

    return (
        <div className="delete-page">
            <div className="container">
                <div className="page-header">
                    <h1>删除 PDF 页面</h1>
                    <p>选择要删除的页面，生成新的 PDF 文件</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        {!taskId ? (
                            <>
                                <div className="upload-section">
                                    <FileUpload
                                        onFilesSelected={handleFilesSelected}
                                        multiple={false}
                                        maxFiles={1}
                                    />
                                </div>

                                {files.length > 0 && (
                                    <>
                                        <PDFPageSelector
                                            file={files[0]}
                                            selectedPages={selectedPages}
                                            onSelectionChange={handlePageSelectionChange}
                                            mode="multiple"
                                        />

                                        <div className="action-section">
                                            <div className="file-count">
                                                已选择文件: <strong>{files[0].name}</strong>
                                            </div>

                                            <Button
                                                variant="danger"
                                                size="lg"
                                                onClick={handleDelete}
                                                isLoading={isCreating}
                                                disabled={selectedPages.length === 0 || isCreating}
                                                icon={<Trash2 />}
                                            >
                                                {isCreating ? '正在准备删除...' : `确认删除 (${selectedPages.length} 页)`}
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {processError && (
                                    <div className="message message-error" style={{ marginTop: '15px' }}>
                                        {processError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                        )}
                    </Card>

                    <div className="tips-section">
                        <Card>
                            <h3>💡 使用提示</h3>
                            <ul>
                                <li>点击页面缩略图选择要删除的页面</li>
                                <li>可以选择多个页面进行批量删除</li>
                                <li>使用"全选"和"清空"按钮快速操作</li>
                                <li>删除后的文件会保留原有页面顺序</li>
                                <li>处理后的文件大时，处理可能会需要一段时间，请耐心等待</li>
                            </ul>
                        </Card>
                    </div>

                    <ConversionHistory operationFilter="delete_pages" onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default DeletePages;
